import React from 'react';
import Seo from '../components/Seo';
import { FadeIn, SlideInLeft, SlideInRight, ScaleIn } from '../components/ScrollReveal';
import { 
  Award, 
  Users, 
  CheckCircle, 
  Star,
  Phone,
  Building,
  Wrench,
  Heart,
  Shield,
  Target,
  HeartHandshake,
  ArrowRight,
  Quote
} from 'lucide-react';

const AboutPage = () => {
  const stats = [
    {
      icon: Award,
      number: '20+',
      label: 'Jahre Erfahrung',
      description: 'Jahrzehntelange Expertise im Fliesenlegerhandwerk'
    },
    {
      icon: Users,
      number: '500+',
      label: 'Zufriedene Kunden',
      description: 'Erfolgreiche Projekte in ganz Norddeutschland'
    },
    {
      icon: Building,
      number: '1000+',
      label: 'Abgeschlossene Projekte',
      description: 'Von Privathäusern bis zu Gewerbeobjekten'
    },
    {
      icon: CheckCircle,
      number: '5 Jahre',
      label: 'Garantie',
      description: 'Vollumfängliche Gewährleistung auf alle Arbeiten'
    }
  ];

  const milestones = [
    {
      year: '2003',
      title: 'Gründung des Unternehmens',
      description: 'Start als selbstständiger Fliesenleger in Cuxhaven mit dem Fokus auf Qualität und Kundenzufriedenheit.'
    },
    {
      year: '2012',
      title: 'Spezialisierung auf Naturstein',
      description: 'Erweiterung der Kompetenzen um exklusive Natursteinarbeiten und anspruchsvolle Designprojekte.'
    },
    {
      year: '2018',
      title: 'Fokus auf barrierefreie Bäder',
      description: 'Zusätzliche Qualifikation für altersgerechte und behindertengerechte Badezimmer-Gestaltung.'
    },
    {
      year: '2024',
      title: 'Über 500 zufriedene Kunden',
      description: 'Erreichen eines wichtigen Meilensteins mit kontinuierlich wachsender Kundenzufriedenheit.'
    }
  ];

  const expertise = [
    {
      icon: Wrench,
      title: 'Fliesenlegerhandwerk',
      description: 'Zertifizierter Experte im Fliesenlegerhandwerk mit jahrzehntelanger Erfahrung.'
    },
    {
      icon: Heart,
      title: 'Barrierefreie Bäder',
      description: 'Spezialist für altersgerechte und behindertengerechte Badezimmer-Gestaltung nach DIN-Normen.'
    },
    {
      icon: Star,
      title: 'Naturstein-Expertise',
      description: 'Langjährige Erfahrung mit Marmor, Granit, Travertin und anderen exklusiven Natursteinen.'
    },
    {
      icon: Shield,
      title: 'Qualitätsgarantie',
      description: '5 Jahre Vollgarantie auf alle Arbeiten und ausschließlich hochwertige Markenmaterialien.'
    }
  ];

  const values = [
    {
      icon: Target,
      title: 'Präzision',
      description: 'Millimetergenaue Arbeit und perfekte Fugenbilder sind mein Markenzeichen.'
    },
    {
      icon: HeartHandshake,
      title: 'Zuverlässigkeit',
      description: 'Termingerechte Ausführung und transparente Kommunikation während des gesamten Projekts.'
    },
    {
      icon: Heart,
      title: 'Leidenschaft',
      description: 'Jedes Projekt wird mit der gleichen Hingabe und Aufmerksamkeit für Details behandelt.'
    }
  ];

  return (
    <>
      <Seo 
        title="Über Mich - Jarek Jozwiak, Ihr Fliesenleger aus Cuxhaven"
        description="Lernen Sie Jarek Jozwiak kennen - Ihren erfahrenen Fliesenleger aus Cuxhaven. Über 20 Jahre Erfahrung, 500+ zufriedene Kunden und Spezialist für hochwertige Fliesenarbeiten."
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-stone-950 py-20 text-white sm:py-24">
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary-600/20 blur-3xl" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="relative mx-auto max-w-4xl text-center">
            <FadeIn>
              <div>
                <div className="mb-6 inline-flex items-center rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-primary-200">
                  <Award className="w-4 h-4 mr-2" />
                  Fliesenlegerbetrieb seit 2008
                </div>
                <h1 className="mb-6 text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                  Handwerk mit Haltung.
                  <span className="mt-2 block text-primary-300">Persönlich geführt.</span>
                </h1>
                <p className="mx-auto mb-9 max-w-3xl text-lg leading-relaxed text-stone-300 sm:text-xl">
                  Seit über 20 Jahren steht Jarek Jozwiak für präzise Fliesenarbeiten, klare Absprachen
                  und eine saubere Ausführung – von der ersten Idee bis zur fertigen Fläche.
                </p>
                <div className="flex flex-col justify-center gap-4 sm:flex-row">
                  <a 
                    href="/#contact" 
                    className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-8 py-4 text-lg font-semibold text-white transition-colors duration-300 hover:bg-primary-500"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Persönliche Beratung
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                  <a 
                    href="/#gallery" 
                    className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/5 px-8 py-4 text-lg font-semibold text-white transition-colors duration-300 hover:bg-white/10"
                  >
                    Meine Arbeiten ansehen
                  </a>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-light text-stone-800 mb-4">
                Zahlen, die für sich sprechen
              </h2>
              <p className="text-lg text-stone-600 max-w-3xl mx-auto">
                Über zwei Jahrzehnte Erfahrung und hunderte zufriedene Kunden sind das Fundament meines Erfolgs.
              </p>
            </div>
          </FadeIn>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <ScaleIn key={index} delay={index * 100}>
                <div className="text-center bg-stone-50 p-6 rounded-xl hover:shadow-lg transition-shadow duration-300">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <stat.icon className="w-8 h-8 text-primary-600" />
                  </div>
                  <div className="text-3xl font-bold text-stone-800 mb-2">{stat.number}</div>
                  <div className="text-lg font-semibold text-stone-700 mb-2">{stat.label}</div>
                  <div className="text-sm text-stone-600">{stat.description}</div>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 sm:py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <SlideInLeft>
              <div>
                <h2 className="text-3xl sm:text-4xl font-light text-stone-800 mb-6">
                  Meine Geschichte
                </h2>
                <div className="space-y-6 text-lg text-stone-600 leading-relaxed">
                  <p>
                    Schon in jungen Jahren faszinierte mich die Präzision und Schönheit des Fliesenlegerhandwerks. 
                    Nach meiner Ausbildung und jahrelanger Erfahrung in verschiedenen Betrieben wagte ich 2003 
                    den Schritt in die Selbstständigkeit.
                  </p>
                    <p>
                    Besonders stolz bin ich auf meine Spezialisierung im Bereich barrierefreier Bäder. Hier kann ich 
                    Menschen dabei helfen, in ihrem Zuhause länger selbstständig und komfortabel zu leben.
                  </p>
                </div>

                <div className="mt-8 p-6 bg-white rounded-xl border border-stone-200">
                  <Quote className="w-8 h-8 text-primary-500 mb-4" />
                  <p className="text-lg text-stone-700 italic mb-4">
                    "Handwerk ist für mich nicht nur Beruf, sondern Berufung. Jede perfekt verlegte Fliese, 
                    jede millimetergenaue Fuge ist ein kleines Kunstwerk."
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-bold">J</span>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800">Jarek Jozwiak</p>
                      <p className="text-sm text-stone-600">Fliesenleger</p>
                    </div>
                  </div>
                </div>
              </div>
            </SlideInLeft>

            <SlideInRight>
              <div>
                <h3 className="text-2xl font-semibold text-stone-800 mb-8">Meine Expertise</h3>
                <div className="space-y-6">
                  {expertise.map((item, index) => (
                    <div key={index} className="flex items-start space-x-4 bg-white p-6 rounded-xl border border-stone-200 hover:shadow-md transition-shadow duration-300">
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-6 h-6 text-primary-600" />
                      </div>
                      <div> 
                        <h4 className="text-lg font-semibold text-stone-800 mb-2">{item.title}</h4>
                        <p className="text-stone-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </SlideInRight>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-light text-stone-800 mb-4">
                Mein Werdegang
              </h2>
              <p className="text-lg text-stone-600">
                Wichtige Meilensteine auf dem Weg zum erfolgreichen Betrieb
              </p>
            </div>
          </FadeIn>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-stone-200"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <SlideInLeft key={index} delay={index * 100}>
                  <div className="relative flex items-start space-x-8">
                    <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold relative z-10">
                      {milestone.year}
                    </div>
                    <div className="flex-1 bg-stone-50 p-6 rounded-xl">
                      <h3 className="text-xl font-semibold text-stone-800 mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-stone-600">{milestone.description}</p>
                    </div>
                  </div>
                </SlideInLeft>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 sm:py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-light text-stone-800 mb-4">
                Meine Werte
              </h2>
              <p className="text-lg text-stone-600 max-w-3xl mx-auto">
                Diese Grundsätze leiten mich bei jedem Projekt und sind die Basis für langfristige Kundenzufriedenheit.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <ScaleIn key={index} delay={index * 100}>
                <div className="text-center bg-white p-8 rounded-xl hover:shadow-lg transition-shadow duration-300">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-stone-800 mb-4">{value.title}</h3>
                  <p className="text-stone-600">{value.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl font-light mb-6">
              Lassen Sie uns Ihr Projekt besprechen
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Ich freue mich darauf, Ihre Wünsche kennenzulernen und gemeinsam die perfekte Lösung zu finden.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/#contact" 
                className="inline-flex items-center justify-center bg-white text-primary-600 px-8 py-4 rounded-xl hover:bg-stone-50 transition-colors duration-300 font-semibold text-lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                Jetzt kontaktieren
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
              <a 
                href="/#gallery" 
                className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-xl hover:bg-white hover:text-primary-600 transition-colors duration-300 font-semibold text-lg"
              >
                Referenzen ansehen
              </a>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
